import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { useCustomFields, CustomFieldsForm, CustomFieldsFilter } from '../../components/CustomFieldsRenderer';
import ExportButton from '../../components/ExportButton';
import PDFExportButton from '../../components/PDFExportButton';
import { toast } from 'sonner';

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customFilterValues, setCustomFilterValues] = useState({});
  const customFields = useCustomFields('vendors');
  const [customValues, setCustomValues] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gstin: '',
    pan: '',
    address: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    payment_terms: 'Net 30'
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await api.get('/purchase/vendors');
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name,
      email: vendor.email,
      phone: vendor.phone,
      gstin: vendor.gstin || '',
      pan: vendor.pan || '',
      address: vendor.address,
      payment_terms: vendor.payment_terms
    });
    setCustomValues(vendor.custom_fields || {});
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingVendor(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      gstin: '',
      pan: '',
      address: {
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      },
      payment_terms: 'Net 30'
    });
    setCustomValues({});
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVendor) {
        await api.put(`/purchase/vendors/${editingVendor.id}`, formData);
        if (Object.keys(customValues).length > 0) {
          await api.put(`/config/custom-field-values/vendors/${editingVendor.id}`, customValues);
        }
      } else {
        const res = await api.post('/purchase/vendors', formData);
        if (Object.keys(customValues).length > 0 && res.data?.id) {
          await api.put(`/config/custom-field-values/vendors/${res.data.id}`, customValues);
        }
      }
      setShowModal(false);
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vendor');
    }
  };

  const deleteVendor = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete vendor "${name}"?`)) return;
    try {
      await api.delete(`/purchase/vendors/${id}`);
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete vendor');
    }
  };

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch =
      vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.vendor_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vendor.gstin && vendor.gstin.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || vendor.status === statusFilter;
    const matchesCustom = Object.entries(customFilterValues).every(([key, val]) => {
      if (!val) return true;
      return String(vendor.custom_fields?.[key] || '').toLowerCase().includes(String(val).toLowerCase());
    });
    return matchesSearch && matchesStatus && matchesCustom;
  });

  return (
    <div data-testid="vendors-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Vendor Master</h1>
        <div className="flex gap-2">
          <ExportButton endpoint="/export/vendors" filename="vendors_export.xlsx" />
          <PDFExportButton endpoint="/export/pdf/vendors" filename="vendors_export.pdf" />
          <button onClick={openCreateModal} className="btn btn-primary" data-testid="add-vendor-btn">
            <Plus size={16} />
            Add Vendor
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="grid grid-cols-2" style={{ gap: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
              <input
                type="text"
                className="form-input"
                placeholder="Search by name, email, code, or GSTIN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '40px' }}
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="status-filter"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <CustomFieldsFilter fields={customFields} values={customFilterValues} onChange={setCustomFilterValues} />
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div data-testid="loading-indicator">Loading vendors...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>GSTIN</th>
                  <th>City</th>
                  <th>Payment Terms</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVendors.map((vendor) => (
                  <tr key={vendor.id} data-testid={`vendor-row-${vendor.vendor_code}`}>
                    <td className="font-mono">{vendor.vendor_code}</td>
                    <td>{vendor.name}</td>
                    <td className="text-sm">{vendor.email}</td>
                    <td className="text-sm">{vendor.phone}</td>
                    <td className="font-mono text-sm">{vendor.gstin || '-'}</td>
                    <td>{vendor.address?.city}</td>
                    <td className="text-sm">{vendor.payment_terms}</td>
                    <td>
                      <span className={`badge ${vendor.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                        {vendor.status}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openEditModal(vendor)} className="btn btn-ghost btn-sm" title="Edit" data-testid={`edit-btn-${vendor.vendor_code}`}>
                          <Edit size={14} />
                        </button>
                        <button onClick={() => deleteVendor(vendor.id, vendor.name)} className="btn btn-ghost btn-sm" title="Delete" data-testid={`delete-btn-${vendor.vendor_code}`}>
                          <Trash2 size={14} color="#DC2626" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="vendor-modal">
          <div className="modal-content" style={{ maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>
                {editingVendor ? 'Edit Vendor' : 'Add Vendor'}
              </h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input type="text" className="form-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="name-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input type="email" className="form-input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="email-input" />
                  </div>
                </div>

                <div className="grid grid-cols-3">
                  <div className="form-group">
                    <label className="form-label">Phone *</label>
                    <input type="text" className="form-input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required data-testid="phone-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">GSTIN</label>
                    <input type="text" className="form-input" value={formData.gstin} onChange={(e) => setFormData({ ...formData, gstin: e.target.value })} data-testid="gstin-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">PAN</label>
                    <input type="text" className="form-input" value={formData.pan} onChange={(e) => setFormData({ ...formData, pan: e.target.value })} data-testid="pan-input" />
                  </div>
                </div>

                <h3 style={{ fontSize: '14px', fontWeight: 600, marginTop: '16px', marginBottom: '8px' }}>Address</h3>
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Address Line 1 *</label>
                    <input type="text" className="form-input" value={formData.address.address_line1} onChange={(e) => setFormData({ ...formData, address: { ...formData.address, address_line1: e.target.value } })} required data-testid="address1-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Address Line 2</label>
                    <input type="text" className="form-input" value={formData.address.address_line2} onChange={(e) => setFormData({ ...formData, address: { ...formData.address, address_line2: e.target.value } })} data-testid="address2-input" />
                  </div>
                </div>

                <div className="grid grid-cols-3">
                  <div className="form-group">
                    <label className="form-label">City *</label>
                    <input type="text" className="form-input" value={formData.address.city} onChange={(e) => setFormData({ ...formData, address: { ...formData.address, city: e.target.value } })} required data-testid="city-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State *</label>
                    <input type="text" className="form-input" value={formData.address.state} onChange={(e) => setFormData({ ...formData, address: { ...formData.address, state: e.target.value } })} required data-testid="state-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Pincode *</label>
                    <input type="text" className="form-input" value={formData.address.pincode} onChange={(e) => setFormData({ ...formData, address: { ...formData.address, pincode: e.target.value } })} required data-testid="pincode-input" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Payment Terms</label>
                  <select className="form-select" value={formData.payment_terms} onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })} data-testid="payment-terms-select">
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 45">Net 45</option>
                    <option value="Net 60">Net 60</option>
                  </select>
                </div>

                <CustomFieldsForm fields={customFields} values={customValues} onChange={setCustomValues} />
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary" data-testid="cancel-btn">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-vendor-btn">
                  {editingVendor ? 'Update Vendor' : 'Add Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vendors;
